import React from 'react';
import { useIntl } from 'react-intl';
import { gql, useQuery } from '@apollo/client';
import { Target } from 'lucide-react';
import { calculateOwnerDiagnosticScores } from '../../../lib/ownerDiagnosticScoring';
import { pctSymbol } from '../../../utils/formatters';

const READINESS_SCORE_QUERY = gql`
  query ReadinessScoreHeroData {
    getCurrentUser {
      id
      vmi_readiness_score
      diag_owner_answers
      diag_business_answers
      diag_completed_at
      triggering_events { event_type completed_at }
    }
  }
`;

const ReadinessScoreHero = ({ className = '' }) => {
  const intl = useIntl();
  const { data, loading, error } = useQuery(READINESS_SCORE_QUERY, { fetchPolicy: 'cache-first' });

  if (loading) {
    return (
      <div className={`bg-succession-secondary rounded-2xl shadow-succession-lg border border-succession-border p-8 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-white bg-opacity-20 rounded w-48 mb-4"></div>
          <div className="h-32 bg-white bg-opacity-20 rounded w-64"></div>
        </div>
      </div>
    );
  }

  const u = data?.getCurrentUser || {};
  const hasVmiScore = u?.vmi_readiness_score != null && u.vmi_readiness_score > 0;
  const hasDiagCompleted = u?.diag_completed_at != null;
  const hasAnyCompleted = Array.isArray(u?.triggering_events) && u.triggering_events.some((e) => e && e.completed_at);
  const hasAnswers = (Array.isArray(u?.diag_owner_answers) && u.diag_owner_answers.some(a => a != null && a > 0)) ||
                      (Array.isArray(u?.diag_business_answers) && u.diag_business_answers.some(a => a != null && a > 0));

  // Show empty state if no assessment completed
  if (!hasVmiScore && !hasDiagCompleted && !hasAnyCompleted && !hasAnswers) {
    return (
      <div className={`bg-succession-secondary rounded-2xl shadow-succession-lg border border-succession-border overflow-hidden relative group ${className}`}>
        {/* Background Icon */}
        <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:opacity-15 transition-opacity">
          <Target className="w-32 h-32 text-white" strokeWidth={1.5} />
        </div>
        <div className="relative z-10 p-8">
          <div className="flex flex-col lg:flex-row gap-8 items-center lg:items-start">
            <div className="flex-1">
              <p className="text-white text-opacity-90 text-base mb-6">
                {intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.complete_assessment_prompt' })}
              </p>
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 rounded-md bg-white text-succession-primary hover:bg-opacity-90 transition-all text-base font-medium"
                onClick={() => {
                  try {
                    if (window.ahoy?.track) window.ahoy.track('succession_readiness_hero_start_assessment_clicked');
                  } catch(_) {}
                  window.location.href = '/succession/plans';
                }}
              >
                {intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.start_succession_plan' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Use VMI readiness score from PlanBuilder if available (source of truth)
  // Fall back to legacy 12-question diagnostic calculation for older assessments
  let score = 0;
  if (hasVmiScore) {
    // PlanBuilder VMI score (source of truth)
    score = u.vmi_readiness_score;
  } else {
    // Legacy calculation for WizardOverlay users
    const scores = calculateOwnerDiagnosticScores(
      u?.diag_owner_answers || [],
      u?.diag_business_answers || []
    );
    score = scores.overallReadinessScore;
  }

  // Calculate percentile message
  const getPercentileMessage = (s) => {
    if (s >= 88) return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.percentile_top_25' });
    if (s >= 75) return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.percentile_top_40' });
    if (s >= 60) return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.percentile_top_50' });
    if (s >= 45) return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.percentile_above_avg' });
    return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.percentile_room_for_improvement' });
  };

  // Get contextual messaging
  const getContextMessage = (s) => {
    if (s >= 88) return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.context_top_25' });
    if (s >= 75) return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.context_top_40' });
    if (s >= 60) return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.context_top_50' });
    if (s >= 45) return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.context_above_avg' });
    return intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.context_needs_work' });
  };

  return (
    <div className={`bg-succession-secondary rounded-2xl shadow-succession-lg border border-succession-border overflow-hidden relative group ${className}`}>
      {/* Background Icon */}
      <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:opacity-15 transition-opacity">
        <Target className="w-32 h-32 text-white" strokeWidth={1.5} />
      </div>

      <div className="relative z-10 p-8 lg:p-10">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Left side - Score display */}
          <div className="flex-1">
            <div className="flex flex-col">
              <div className="text-white font-bold mb-4" style={{ fontSize: '6rem', lineHeight: '1', letterSpacing: '-0.02em' }}>
                {score}{pctSymbol()}
              </div>
              <p className="text-white text-lg font-medium">
                {getPercentileMessage(score)}
              </p>
            </div>
          </div>

          {/* Right side - What this means */}
          <div className="flex-1 bg-white bg-opacity-10 rounded-lg p-6 backdrop-blur-sm">
            <h3 className="text-white text-lg font-semibold mb-3">{intl.formatMessage({ id: 'succession.value_acceleration.readiness_score.what_this_means' })}</h3>
            <p className="text-white text-opacity-90 leading-relaxed">
              {getContextMessage(score)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadinessScoreHero;

